import type { FormikProps, FormikValues } from 'formik';
import React from 'react';
import Buttons from './Buttons';
import type { FormProps } from './Form';
import Form, { Submit } from './Form';
import type {
  ModalFooterButtonBaseProps,
  ModalFooterLeftButtonProps,
  ModalFooterSecondaryButtonProps,
} from './Modal';
import { BaseModal, ModalButton, ModalFooterContainer } from './Modal';
import ModalPrimitive from './ModalPrimitive';

interface FormModalFooterProps {
  submitButton: ModalFooterButtonBaseProps;
  secondaryButton?: ModalFooterSecondaryButtonProps;
  leftButton?: ModalFooterLeftButtonProps;
}

const FormModalFooter = ({
  submitButton: { text: submitButtonText, ...submitButtonProps },
  secondaryButton,
  leftButton,
}: FormModalFooterProps) => (
  <ModalFooterContainer>
    <ModalPrimitive.Footer
      left={leftButton && <ModalButton {...leftButton} />}
      right={
        <Buttons>
          {secondaryButton && (
            <ModalButton {...secondaryButton} variant="secondary" />
          )}
          <Submit {...submitButtonProps} size="regular" variant="primary">
            {submitButtonText}
          </Submit>
        </Buttons>
      }
    />
  </ModalFooterContainer>
);

interface FormModalProps<Values extends FormikValues>
  extends Omit<React.ComponentProps<typeof BaseModal>, 'children'> {
  children:
    | React.ReactNode
    | ((formik: FormikProps<Values>) => React.ReactNode);
  form: FormProps<Values>;
  footer:
    | FormModalFooterProps
    | ((formik: FormikProps<Values>) => FormModalFooterProps);
}

const FormModal = <Values extends FormikValues>({
  children,
  form,
  footer,
  ...modalProps
}: FormModalProps<Values>) => {
  return (
    <BaseModal {...modalProps}>
      <Form<Values> {...form}>
        {(props) => (
          <>
            {typeof children === 'function' ? children(props) : children}
            {typeof footer === 'function' ? (
              <FormModalFooter {...footer(props)} />
            ) : (
              <FormModalFooter {...footer} />
            )}
          </>
        )}
      </Form>
    </BaseModal>
  );
};

export default FormModal;
